import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { UpdateReport } from 'src/api';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { File } from 'src/components/moleculesLegacy';
import { AddPerpetratorModal } from 'src/components/organismsLegacy/AddPerpetratorModal/AddPerpetratorModal';
import { DraftEvents } from 'src/components/organismsLegacy/DraftEvents/DraftEvents';
import {
  fetchDraftReport,
  updateCurrentEvent,
  updateReport as updateReportDetails,
} from 'src/creators/MCC.creators';
import { UserType } from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import { AppDispatch } from 'src/store';
import { themeOptions } from 'src/theme/theme';
import * as Types from 'src/types';
import { FeatureFlag, MCCEventType } from 'src/types';
import { canManageCases } from 'src/utils/canManageCases';
import { getRandomId } from 'src/utils/commonFunctions';
import {
  AddEventModal,
  AddReporterModal,
  CreateReportHeader,
  DraftAuditLog,
  DraftReportDetails,
} from '../../organismsLegacy';
import { DraftReportTabs } from '../../organismsLegacy/DraftReportTabs/DraftReportTabs';
import { Body, Container, Left, Right } from './CreateReport.styles';

export const CreateReport = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [, route, reportID] = location.pathname.split('/');
  const isExistingReport = route === 'create-existing-report';
  const [headerIndex, setHeaderIndex] = useState(0);
  const [isAddingEvent, setIsAddingEvent] = useState(false);
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [isAddingReporter, setIsAddingReporter] = useState(false);
  const [isAddingPerpetrator, setIsAddingPerpetrator] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const assignee = useSelector(
    (state: Types.State) =>
      state?.manualCaseCreation?.mccReducer?.currentReport?.assigneeName ??
      null,
  );
  const currentReport = useSelector(
    (state: Types.State) =>
      state?.manualCaseCreation?.mccReducer?.currentReport ?? null,
  );

  const features = useSelector((state: Types.State) => state?.features || []);

  const isUpdateRelationshipToOrganisationEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.RELATIONSHIP_TO_ORG,
  );

  // TODO: REMOVE THIS SECTION when re-enabling delete/submit/update events functionality
  const isReportIntakeImprovementsEnabled = useIsFeatureEnabled(
    FeatureFlag.REPORT_INTAKE_IMPROVEMENTS,
  );
  const isUseNormalizedReportDataEnabled = useIsFeatureEnabled(
    FeatureFlag.USE_NORMALIZED_REPORT_DATA,
  );
  const isReportIntakeImprovementsDraftEnabled = useIsFeatureEnabled(
    FeatureFlag.REPORT_INTAKE_IMPROVEMENTS_DRAFT,
  );

  const areDraftReportFlagsEnabled =
    isReportIntakeImprovementsDraftEnabled &&
    isUseNormalizedReportDataEnabled &&
    isReportIntakeImprovementsEnabled;
  // END TODO: Remove the above feature flag checks
  // After removal, also remove the props passed to CreateReportHeader, DraftEvents, and modal components below

  const currentUser = useSelector(
    (state: Types.State) => state?.globalUsers.currentUser as Types.User,
  );

  const cannotManageCases = !canManageCases(currentUser as UserType);

  const updateReport = async (id: string, report: UpdateReport) => {
    await dispatch(updateReportDetails(id, report));
  };

  useEffect(() => {
    const fetchDraft = async () => await dispatch(fetchDraftReport(reportID));
    fetchDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportID, currentReport?.user, currentReport?.userUpdates]);

  const usersToExclude = [];
  if (currentReport?.assignee) {
    usersToExclude.push(currentReport.assignee);
  }
  if (currentReport?.User?.id) {
    usersToExclude.push(currentReport.User.id);
  }
  if (currentUser?.id) {
    usersToExclude.push(+currentUser.id);
  }

  const updatePerpetrator = ({
    selectedUser,
    externalName,
    currentEvent,
    internal,
  }: {
    selectedUser: UserType | null;
    externalName: string;
    currentEvent?: MCCEventType | null;
    internal: boolean;
  }) => {
    if ((selectedUser || externalName) && currentEvent) {
      const timestamp = new Date().toISOString();
      dispatch(
        updateCurrentEvent(currentEvent, 'perpetrators', [
          ...currentEvent.perpetrators,
          {
            name: internal ? selectedUser?.name : externalName,
            id: internal ? (selectedUser?.id ?? getRandomId()) : getRandomId(),
            external: !internal,
            created: timestamp,
            updated: timestamp,
          },
        ]),
      );
    }
    setIsAddingPerpetrator(false);
    setIsAddingEvent(true);
  };

  return (
    <Container>
      <CreateReportHeader
        reportID={reportID}
        currentReport={currentReport}
        isExistingReport={isExistingReport}
        currentUser={currentUser}
        updateReport={updateReport}
        areDraftReportFlagsEnabled={areDraftReportFlagsEnabled}
      />
      <Body>
        <Left>
          <DraftReportDetails
            isExistingReport={isExistingReport}
            reportID={reportID}
            theme={themeOptions}
            currentManager={{ name: assignee ?? '' }}
            currentReport={currentReport}
            cannotManageCases={cannotManageCases}
            updateReport={updateReport}
            setIsAddingReporter={setIsAddingReporter}
            isUpdateRelationshipToOrganisationEnabled={
              isUpdateRelationshipToOrganisationEnabled
            }
          />
        </Left>
        <Right>
          <DraftReportTabs
            headers={['Events', 'Audit Log']}
            headerIndex={headerIndex}
            setHeaderIndex={setHeaderIndex}
            headerComponent={
              [
                <DraftEvents
                  reportID={reportID}
                  currentReport={currentReport}
                  updateReport={updateReport}
                  setIsAddingEvent={setIsAddingEvent}
                  setIsEditingEvent={setIsEditingEvent}
                  disabled={areDraftReportFlagsEnabled} // TODO: REMOVE this prop when re-enabling events
                  key="events"
                />,
                <DraftAuditLog
                  reportID={reportID}
                  noMargin={true}
                  key="audit-log"
                />,
              ][headerIndex]
            }
          />
        </Right>
      </Body>
      <AddReporterModal
        reportID={reportID}
        currentReport={currentReport}
        open={isAddingReporter}
        setOpen={setIsAddingReporter}
        updateReport={updateReport}
        isUpdateRelationshipToOrganisationEnabled={
          isUpdateRelationshipToOrganisationEnabled
        }
      />
      {/* TODO: REMOVE "!areDraftReportFlagsEnabled" from open prop when re-enabling events */}
      <AddEventModal
        reportID={reportID}
        open={isAddingEvent && !areDraftReportFlagsEnabled}
        setOpen={setIsAddingEvent}
        setIsAddingPerpetrator={setIsAddingPerpetrator}
        editingEvent={isEditingEvent}
        files={files}
        setFiles={setFiles}
      />
      {/* TODO: REMOVE "&& !areDraftReportFlagsEnabled" from open prop when re-enabling events */}
      <AddPerpetratorModal
        open={isAddingPerpetrator && !areDraftReportFlagsEnabled}
        setOpen={setIsAddingPerpetrator}
        usersToExclude={usersToExclude}
        callback={updatePerpetrator}
      />
    </Container>
  );
};
